import { defineStore } from "pinia";
import { ref, computed } from "vue";
import ApiPaths from "@/apis/ApiPaths";
import ApiPathGroups from "@/apis/ApiPathGroups";
import { usePromptStore } from "./promptStore";
import { catchable, exceptionWrapper } from "./piniaUtils";
import { useToastsStore } from "./toastsStore";

export const useAllPromptsStore = defineStore("all-prompts", () => {
  const promptStore = usePromptStore();
  const toastsStore = useToastsStore();
  const prompts = ref([]);
  const promptGroups = ref([]);

  const getPromptGroupsLoading = ref(false);

  const mutateRetentions = retentionsIn => {
    prompts.value = retentionsIn;
    if (promptStore.prompt) {
      const newItem = prompts.value.find(({ id }) => id === promptStore.prompt.id);
      if (newItem) promptStore.prompt = newItem;
    }
  };

  const setPriorityRetentions = ({ appId, pathGroups }) =>
    catchable({
      t: async () => mutateRetentions(await ApiPaths.setPriorityRetentions(appId, pathGroups)),
      c: error => toastsStore.create({ type: "error", body: `${error}. Please try again.` }),
      loadable: true,
    });

  const setOrderRetentions = ({ appId, paths }) =>
    catchable({
      t: async () => {
        mutateRetentions(await ApiPaths.setOrderRetentions(appId, paths));
        if (promptStore.promptGroup)
          promptStore.promptGroup = { ...promptStore.promptGroup, paths };
      },
      loadable: true,
      throwable: true,
    });

  const getPromptsLoading = ref(false);
  const getPrompts = (appId, { nonBlocking } = { nonBlocking: false }) => {
    const fn = async () => mutateRetentions(await ApiPaths.getRetentions(appId));
    return nonBlocking
      ? exceptionWrapper(fn, { loading: getPromptsLoading })
      : catchable({ t: fn, loadable: true });
  };

  const sortedAllPrompts = computed(() => {
    return [...prompts.value].sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : b.name.toLowerCase() > a.name.toLowerCase()
          ? -1
          : 0,
    );
  });

  const getPromptGroups = (appId, { nonBlocking } = { nonBlocking: false }) => {
    const fn = async () => (promptGroups.value = (await ApiPathGroups.getPathGroups(appId)) || []);
    return nonBlocking
      ? exceptionWrapper(fn, { loading: getPromptGroupsLoading })
      : catchable({ t: fn, loadable: true });
  };

  const cloneRfLitePaths = ({ appId, path_ids }) =>
    catchable({
      t: async () => {
        const paths = await ApiPaths.cloneRfLitePaths(appId, { path_ids });
        getPrompts(appId);
        getPromptGroups(appId);
        return paths;
      },
      loadable: true,
    });

  return {
    prompts,
    promptGroups,
    getPrompts,
    getPromptsLoading,
    sortedAllPrompts,
    setPriorityRetentions,
    setOrderRetentions,
    cloneRfLitePaths,
    getPromptGroups,
    getPromptGroupsLoading,
  };
});
